/**
 * Kitchen Staff Management Page for HMH Lunch Ordering System
 *
 * Allows admins to manage kitchen staff accounts and PINs.
 */

import { useState } from "react";
import {
  useKitchenStaffList,
  useCreateKitchenStaff,
  useUpdateKitchenStaff,
  useDeleteKitchenStaff,
  useResetPin,
  KitchenStaff,
} from "../../hooks/useKitchenStaff";
import { KitchenTabletsPanel } from "../../components/KitchenTabletsPanel";

export function KitchenStaffPage() {
  const { data, isLoading, error } = useKitchenStaffList();
  const createMutation = useCreateKitchenStaff();
  const updateMutation = useUpdateKitchenStaff();
  const deleteMutation = useDeleteKitchenStaff();
  const resetPinMutation = useResetPin();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<KitchenStaff | null>(null);
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);

  // Form states
  const [createUsername, setCreateUsername] = useState("");
  const [createDisplayName, setCreateDisplayName] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createMutation.mutateAsync({
        username: createUsername,
        display_name: createDisplayName,
      });

      setGeneratedPin(result.pin);
      setShowCreateModal(false);
      setShowPinModal(true);
      setCreateUsername("");
      setCreateDisplayName("");
    } catch (error) {
      alert(`Failed to create staff: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleEditStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStaff) return;

    try {
      await updateMutation.mutateAsync({
        id: selectedStaff.id,
        data: {
          display_name: editDisplayName,
          is_active: editIsActive,
        },
      });

      setShowEditModal(false);
      setSelectedStaff(null);
    } catch (error) {
      alert(`Failed to update staff: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleDeleteStaff = async (staff: KitchenStaff) => {
    if (!confirm(`Are you sure you want to delete ${staff.display_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(staff.id);
    } catch (error) {
      alert(`Failed to delete staff: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleResetPin = async (staff: KitchenStaff) => {
    if (!confirm(`Are you sure you want to reset the PIN for ${staff.display_name}?`)) {
      return;
    }

    try {
      const result = await resetPinMutation.mutateAsync(staff.id);
      setGeneratedPin(result.pin);
      setShowPinModal(true);
    } catch (error) {
      alert(`Failed to reset PIN: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const openEditModal = (staff: KitchenStaff) => {
    setSelectedStaff(staff);
    setEditDisplayName(staff.display_name);
    setEditIsActive(staff.is_active);
    setShowEditModal(true);
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Staff</h1>
            <p className="text-sm text-gray-600 mt-1">Manage kitchen staff accounts and PINs</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Staff
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading kitchen staff...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            Error loading kitchen staff: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        )}

        {/* Staff Table */}
        {data && data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((staff) => (
                  <tr key={staff.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {staff.username}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {staff.display_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          staff.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {staff.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {staff.last_login
                          ? new Date(staff.last_login).toLocaleDateString()
                          : "Never"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(staff)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleResetPin(staff)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                        disabled={resetPinMutation.isPending}
                      >
                        Reset PIN
                      </button>
                      <button
                        onClick={() => handleDeleteStaff(staff)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {data && data.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              No kitchen staff found. Click "Add Staff" to create a new account.
            </p>
          </div>
        )}
      </div>

      {/* Create Staff Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add Kitchen Staff</h2>
            <form onSubmit={handleCreateStaff}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={createUsername}
                    onChange={(e) => setCreateUsername(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for login (e.g., "jsmith")</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={createDisplayName}
                    onChange={(e) => setCreateDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Full name shown in the system</p>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Kitchen Staff</h2>
            <form onSubmit={handleEditStaff}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={editDisplayName}
                    onChange={(e) => setEditDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {updateMutation.isPending ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PIN Display Modal */}
      {showPinModal && generatedPin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">PIN Generated</h2>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                Important: Write down this PIN. It will only be shown once!
              </p>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-mono font-bold text-gray-900 tracking-widest">
                  {generatedPin}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowPinModal(false);
                setGeneratedPin(null);
              }}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              I've Written Down the PIN
            </button>
          </div>
        </div>
      )}
    </div>
    <KitchenTabletsPanel />
    </>
  );
}
